import React, { useState } from 'react';
import StatsPieChart from '../charts/StatsPieChart';
import StatsBarChart from '../charts/StatsBarChart';
import StatBlock from '../StatBlock';
import { toStatItems, asHectares } from '../statBlockUtils';
import type { YearStats, StatsChartData, RegionStats } from '../statsTypes';
import { resolveRegionStats, canonicalRegionLabel } from '../regionResolver';

const REGIONS = ['NT Total', 'Beaufort Delta', 'Sahtu', 'South Slave', 'North Slave', 'Deh Cho'];

const STATUS_COLORS: Record<string, string> = {
  'Out of Control': '#D32F2F',
  'Being Held': '#F57C00',
  'Under Control': '#FBC02D',
  'Declared Out': '#388E3C',
  'New': '#1565C0',
};

const RESPONSE_COLORS: Record<string, string> = {
  'Full Response': '#1976D2',
  'Modified Response': '#7B1FA2',
  'Monitor': '#00838F',
};

interface CurrentYearTabProps {
  data: YearStats | null;
}

function getRegionStats(data: YearStats, regionName: string): RegionStats | null {
  if (regionName === 'NT Total') return data.totals;
  return resolveRegionStats(data.regions, regionName);
}

function buildCauseData(stats: RegionStats): StatsChartData[] {
  return [
    { name: 'Natural', value: stats.natural_causes, color: '#2E7D32' },
    { name: 'Human', value: stats.human_causes, color: '#C62828' },
  ];
}

function buildStatusData(stats: RegionStats): StatsChartData[] {
  return [
    { name: 'Out of Control', value: stats.out_of_control, color: STATUS_COLORS['Out of Control'] },
    { name: 'Being Held', value: stats.being_held, color: STATUS_COLORS['Being Held'] },
    { name: 'Under Control', value: stats.under_control, color: STATUS_COLORS['Under Control'] },
    { name: 'Declared Out', value: stats.declared_out, color: STATUS_COLORS['Declared Out'] },
    { name: 'New', value: stats.new_fires, color: STATUS_COLORS['New'] },
  ];
}

function buildResponseData(stats: RegionStats): StatsChartData[] {
  return [
    { name: 'Full Response', value: stats.full_response, color: RESPONSE_COLORS['Full Response'] },
    { name: 'Modified Response', value: stats.modified_response, color: RESPONSE_COLORS['Modified Response'] },
    { name: 'Monitor', value: stats.monitor_response, color: RESPONSE_COLORS['Monitor'] },
  ];
}

function buildRegionalComparison(data: YearStats, metric: 'total_fires' | 'total_area'): StatsChartData[] {
  return Object.entries(data.regions).map(([name, stats]) => ({
    name: canonicalRegionLabel(name),
    value: stats[metric],
  }));
}

const CurrentYearTab: React.FC<CurrentYearTabProps> = ({ data }) => {
  const [activeRegion, setActiveRegion] = useState('NT Total');

  if (!data) return <p>No data available</p>;

  const stats = getRegionStats(data, activeRegion);

  return (
    <div className="stats-year-tab">
      <div className="stats-sub-tabs">
        {REGIONS.map(region => (
          <button
            key={region}
            className={`stats-sub-tab ${activeRegion === region ? 'stats-sub-tab--active' : ''}`}
            onClick={() => setActiveRegion(region)}
          >
            {region}
          </button>
        ))}
      </div>
      {stats && (
        <>
        <div className="stats-blocks">
          <StatBlock title="Totals" items={[
            { label: 'Fire Frequency', value: stats.total_fires },
            { label: 'Hectares Burned', value: asHectares(stats.total_area) },
          ]} />
          <StatBlock title="Cause" items={toStatItems(buildCauseData(stats))} />
          <StatBlock title="Status" items={toStatItems(buildStatusData(stats))} />
          <StatBlock title="Response" items={toStatItems(buildResponseData(stats))} />
          <StatBlock title="Regional Fire Count" items={toStatItems(buildRegionalComparison(data, 'total_fires'))} />
          <StatBlock title="Regional Hectares" items={toStatItems(buildRegionalComparison(data, 'total_area'), asHectares)} />
        </div>
        <div className="stats-charts-grid">
          <StatsPieChart data={buildCauseData(stats)} title="Cause Breakdown" />
          <StatsPieChart data={buildStatusData(stats)} title="Status Breakdown" />
          <StatsPieChart data={buildResponseData(stats)} title="Response Breakdown" />
          <StatsBarChart data={buildRegionalComparison(data, 'total_fires')} title="Regional Fire Comparison" color="#1976D2" />
          <StatsBarChart data={buildRegionalComparison(data, 'total_area')} title="Regional Area Comparison" color="#F57C00" />
        </div>
        </>
      )}
    </div>
  );
};

export default CurrentYearTab;
